// Display labels for ez location tokens — the text a location chip or a search
// heading shows for a `search` / `secondary_locations` / `areas` value. The
// client twin of Connect::LocationLabels (Ruby).
//
// A legacy token carries its label: "City:Myrtle Beach, SC" reads "Myrtle Beach,
// SC" once the "Type:" prefix is stripped. Since EZH-3070 ez autocomplete issues
// GAZETTEER tokens — "City:8331365f-2614-4fcf-9a81-d219c42131de" — so stripping
// the prefix leaves a UUID. Their labels are remembered here from the
// suggestions the user picked from, the labels the server seeded, or a labels
// endpoint; until one is known a generic "Selected city" stands in, never the
// UUID.

// Mirrors Connect::LocationLabels::GAZETTEER_PREFIXES (and ez's
// Search::LocationToken): both the prefix AND a UUID value make a gazetteer token.
const GAZETTEER_PREFIXES = new Set(['City', 'County', 'State', 'Neighborhood', 'Community']);
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const FALLBACK_LABELS: Record<string, string> = {
  City: 'Selected city',
  County: 'Selected county',
  State: 'Selected state',
  Neighborhood: 'Selected neighborhood',
  Community: 'Selected community',
};

// gazetteer UUID (lowercased) -> label. Module-level so every picker on the page
// shares what any of them learned.
const known = new Map<string, string>();

const stripExclusion = (token: string): string => (token.startsWith('-') ? token.slice(1) : token);

const fallbackLabel = (token: string): string => {
  const raw = stripExclusion(token.trim());
  return FALLBACK_LABELS[raw.slice(0, raw.indexOf(':'))];
};

/** The lowercased gazetteer UUID a token carries, or null for a legacy token. */
export function gazetteerId(token: unknown): string | null {
  if (typeof token !== 'string') return null;
  const raw = stripExclusion(token.trim());
  const colon = raw.indexOf(':');
  if (colon < 0) return null;
  const value = raw.slice(colon + 1).trim();
  return GAZETTEER_PREFIXES.has(raw.slice(0, colon)) && UUID.test(value) ? value.toLowerCase() : null;
}

export const isGazetteerToken = (token: unknown): boolean => gazetteerId(token) !== null;

type Suggestion = { id?: unknown; text?: unknown };

/**
 * Remember labels from autocomplete suggestions (`[{ id, text }]`) or a
 * `{ token: label }` map (server-seeded / the labels endpoint). Only gazetteer
 * tokens are stored — a legacy token's label is already in the token.
 */
export function rememberLocationLabels(input: Suggestion[] | Record<string, unknown> | null | undefined): void {
  if (!input) return;
  const pairs: Array<[unknown, unknown]> = Array.isArray(input)
    ? input.map((s) => [s?.id, s?.text])
    : Object.entries(input);
  pairs.forEach(([token, label]) => {
    const id = gazetteerId(token);
    const text = typeof label === 'string' ? label.trim() : '';
    // The server's "Selected city" stand-in isn't a name — don't learn it, so a
    // later suggestion or lookup can still supply the real one.
    if (!id || !text || text === fallbackLabel(token as string)) return;
    known.set(id, text);
  });
}

/** Whether `token` can be labelled without a lookup. */
export function hasLocationLabel(token: unknown): boolean {
  const id = gazetteerId(token);
  return id === null || known.has(id);
}

/** The gazetteer tokens in `tokens` whose label isn't known yet (de-duped). */
export function unlabeledLocationTokens(tokens: unknown[] | null | undefined): string[] {
  const out = new Set<string>();
  (tokens || []).forEach((token) => {
    if (typeof token === 'string' && !hasLocationLabel(token)) out.add(stripExclusion(token.trim()));
  });
  return [...out];
}

/**
 * The display label for a location token (an exclusion's "-" is dropped —
 * callers word "Exclude …" themselves). "City:Austin, TX" → "Austin, TX";
 * "City:<uuid>" → its remembered label, or "Selected city" until one is known;
 * free text without a "Type:" prefix passes through.
 */
export function locationLabel(token: unknown): string {
  if (typeof token !== 'string') return '';
  const raw = stripExclusion(token.trim());
  const id = gazetteerId(raw);
  if (id) return known.get(id) || fallbackLabel(raw);
  return /^[A-Za-z]+:/.test(raw) ? raw.slice(raw.indexOf(':') + 1).trim() : raw;
}

/** `{ token: label }` for the gazetteer tokens among `tokens` that have one. */
export function knownLocationLabels(tokens: unknown[] | null | undefined): Record<string, string> {
  const out: Record<string, string> = {};
  (tokens || []).forEach((token) => {
    const id = gazetteerId(token);
    if (id && known.has(id)) out[stripExclusion((token as string).trim())] = known.get(id) as string;
  });
  return out;
}

/** Test helper — forget every remembered label. */
export function resetLocationLabels(): void {
  known.clear();
}
